import { useRef, useCallback, useEffect } from 'react';
import { useQueryClient, type QueryKey } from '@tanstack/react-query';

import { useDebouncedCallback } from './use-debounced-callback';

export interface UseBatchedOptimisticActionOptions<TEntity extends { id: string }> {
  /**
   * Delay in milliseconds before executing the batched action
   * @default 500
   */
  delay?: number;
  /**
   * Maximum number of items to batch before forcing execution
   * When this limit is reached, the API call executes immediately
   * regardless of the delay timer
   * @default undefined (no size limit)
   */
  batchSize?: number;
  /**
   * The API function to call with batched IDs
   */
  apiAction: (ids: string[]) => Promise<any>;
  /**
   * Function to optimistically update a entity
   * Return `null` to remove the entity from the list
   */
  optimisticUpdate: (entity: TEntity) => TEntity | null;
  /**
   * The query key to use for the query client
   */
  queryKey: QueryKey;
}

/**
 * Custom hook to manage batched optimistic actions that are safe from race conditions
 * with concurrent queries (like those with refetchInterval).
 *
 * The race condition occurs when:
 * 1. User performs an action → optimistic update is applied
 * 2. A background query (refetchInterval) fetches data from server (or is already in-flight)
 * 3. Server response overwrites the optimistic update
 *
 * This hook solves it by:
 * 1. Aggressively canceling all queries (including in-flight ones)
 * 2. Providing a way for queries to check if they should be disabled
 * 3. Merging server responses with pending optimistic updates using query data transformation
 */
export const useBatchedOptimisticAction = <TEntity extends { id: string }>({
  apiAction,
  optimisticUpdate,
  queryKey,
  delay = 500,
  batchSize
}: UseBatchedOptimisticActionOptions<TEntity>) => {
  const queryClient = useQueryClient();
  const isMountedRef = useRef(true);

  // Store pending IDs and their original states for rollback
  const pendingIds = useRef<Set<string>>(new Set());
  const originalStates = useRef<Map<string, TEntity>>(new Map());

  // Track if we have any pending operations to prevent race conditions
  const hasPendingOperations = useRef(false);

  // Data transformer that merges server data with pending optimistic updates
  const transformQueryData = useCallback(
    (serverData: TEntity[] | undefined): TEntity[] => {
      if (!serverData || !hasPendingOperations.current || pendingIds.current.size === 0) {
        return serverData || [];
      }

      const pendingIdsArray = Array.from(pendingIds.current);

      // Apply pending optimistic updates to server data
      const transformedData = serverData
        .map(serverEntity => {
          if (pendingIdsArray.includes(serverEntity.id)) {
            if (originalStates.current.has(serverEntity.id)) {
              return optimisticUpdate(serverEntity);
            }
          }
          return serverEntity;
        })
        .filter((entity): entity is TEntity => entity !== null);

      return transformedData;
    },
    [optimisticUpdate]
  );

  const executeBatchedAction = useCallback(async () => {
    const idsToProcess = Array.from(pendingIds.current);
    const statesToRollback = new Map(originalStates.current);

    // Clear pending state
    pendingIds.current.clear();
    originalStates.current.clear();
    hasPendingOperations.current = false;

    if (idsToProcess.length === 0) return;

    try {
      await apiAction(idsToProcess);

      // Refetch the query to get the latest state after successful operation
      queryClient.invalidateQueries({ queryKey: queryKey });
    } catch (error) {
      // Only rollback if component is still mounted (skip rollback during unmount)
      if (!isMountedRef.current) {
        throw error; // Still throw the error, just skip the rollback
      }

      // Rollback optimistic updates
      const currentEntities = queryClient.getQueryData<TEntity[]>(queryKey);

      if (currentEntities && statesToRollback.size > 0) {
        const rolledBackEntities = currentEntities.map(entity => {
          const originalState = statesToRollback.get(entity.id);
          return originalState || entity;
        });

        // Add back any entities that were removed optimistically
        const removedEntities = Array.from(statesToRollback.values()).filter(
          original => !currentEntities.some(current => current.id === original.id)
        );

        queryClient.setQueryData(queryKey, [...rolledBackEntities, ...removedEntities]);
      }

      throw error;
    }
  }, [queryClient, apiAction, queryKey]);

  const debouncedAction = useDebouncedCallback(executeBatchedAction, delay);

  const executeAction = useCallback(
    (id: string) => {
      // AGGRESSIVELY cancel ALL queries with this key (including in-flight requests)
      queryClient.cancelQueries({ queryKey: queryKey });

      // Mark that we have pending operations
      hasPendingOperations.current = true;

      const currentEntities = queryClient.getQueryData<TEntity[]>(queryKey);

      if (currentEntities) {
        const originalEntity = currentEntities.find(entity => entity.id === id);
        if (originalEntity) {
          originalStates.current.set(id, { ...originalEntity });
        }

        const updatedEntities = currentEntities
          .map(entity => {
            if (entity.id === id) {
              return optimisticUpdate(entity);
            }
            return entity;
          })
          .filter((entity): entity is TEntity => entity !== null);

        queryClient.setQueryData(queryKey, updatedEntities);
      }

      pendingIds.current.add(id);

      // Check if we've reached the batch size limit
      if (batchSize && pendingIds.current.size >= batchSize) {
        // Cancel the debounced action and execute immediately
        debouncedAction.cancel();
        executeBatchedAction();
      } else {
        // Use normal debounced execution
        debouncedAction();
      }
    },
    [queryClient, debouncedAction, optimisticUpdate, queryKey, batchSize, executeBatchedAction]
  );

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      hasPendingOperations.current = false;

      if (debouncedAction.isPending()) {
        debouncedAction.flush();
      }
    };
  }, [debouncedAction]);

  /**
   * Returns whether there are pending operations.
   * Use this in your query options to prevent race conditions:
   *
   * @example
   * const { executeAction, hasPendingOperations, transformQueryData } = useBatchedOptimisticAction({...});
   *
   * const { data } = useQuery(
   *   queryKey,
   *   fetchFunction,
   *   {
   *     refetchInterval: 5000,
   *     enabled: !hasPendingOperations(), // Prevent new requests during optimistic updates
   *     select: transformQueryData // Transform server data to preserve optimistic updates
   *   }
   * );
   */
  const getPendingOperationsStatus = useCallback(() => hasPendingOperations.current, []);

  return {
    executeAction,
    hasPendingOperations: getPendingOperationsStatus,
    transformQueryData
  };
};
